import { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { titleBatchesSchema, titleCategories, type TitleBatches } from "@eaf/contracts";
import { useAuth } from "../auth/auth-context";

export function TitleSuggestions({
  artworkId,
  onUse,
  previewUrl,
  previewTitle = "Artwork",
  disabled = false
}: {
  artworkId: string;
  onUse: (title: string) => void;
  previewUrl?: string;
  previewTitle?: string;
  disabled?: boolean;
}) {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [batches, setBatches] = useState<TitleBatches["batches"]>([]);
  const [batchId, setBatchId] = useState("");
  const [selected, setSelected] = useState("");
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), [artworkId]);
  const path = `/curator/artworks/${artworkId}/title-suggestions`;
  const read = async (signal: AbortSignal) => {
    const result = titleBatchesSchema.parse(await auth.authorizedFetch(path, { signal }));
    signal.throwIfAborted();
    setBatches(result.batches);
    return result.batches;
  };
  const show = (sets: TitleBatches["batches"], preferred?: string) => {
    setBatchId(preferred ?? sets[0]?.id ?? "");
    setSelected("");
    setOpen(true);
  };
  const waitFor = async (id: string, signal: AbortSignal) => {
    const until = Date.now() + 330_000;
    while (Date.now() < until) {
      signal.throwIfAborted();
      const sets = await read(signal);
      const batch = sets.find((item) => item.id === id);
      if (batch && !["pending", "running"].includes(batch.state)) {
        show(sets, id);
        if (batch.error) setError(batch.error);
        return;
      }
      await new Promise<void>((resolve, reject) => {
        const abort = () => {
          clearTimeout(timer);
          reject(new DOMException("Title polling stopped", "AbortError"));
        };
        const timer = setTimeout(() => {
          signal.removeEventListener("abort", abort);
          resolve();
        }, 2000);
        signal.addEventListener("abort", abort, { once: true });
      });
    }
    throw new Error(
      "Suggestions are still processing. Ask AI again to check the saved request; this will not start another generation."
    );
  };
  const generate = async (regenerate: boolean, signal: AbortSignal) => {
    const result = await auth.authorizedFetch<{ aiRunId: string }>(path, {
      method: "POST",
      signal,
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ regenerate })
    });
    await waitFor(result.aiRunId, signal);
  };
  const run = async (regenerate = false) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const { signal } = controller;
    setBusy(true);
    setError("");
    try {
      if (regenerate) await generate(true, signal);
      else {
        const sets = await read(signal);
        if (!sets.length) await generate(false, signal);
        else {
          show(sets);
          if (sets[0] && ["pending", "running"].includes(sets[0].state))
            await waitFor(sets[0].id, signal);
        }
      }
    } catch (caught) {
      if (signal.aborted) return;
      setError(
        caught instanceof Error ? caught.message : "Title suggestions are unavailable. Try again."
      );
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  };
  const batch = batches.find((item) => item.id === batchId);
  const hasTitles =
    batch?.groups && titleCategories.some((category) => batch.groups![category].length);
  return (
    <>
      <Button
        size="sm"
        type="button"
        variant="link"
        disabled={busy || disabled}
        onClick={() => void run()}
      >
        {busy ? (
          <>
            <Spinner size="sm" aria-hidden="true" /> Preparing title suggestions…
          </>
        ) : (
          "Ask AI"
        )}
      </Button>
      {!open && error ? (
        <Alert variant="danger" role="alert">
          {error}
        </Alert>
      ) : null}
      <Modal
        show={open}
        onHide={() => setOpen(false)}
        size="lg"
        centered
        scrollable
        aria-labelledby="title-suggestions-heading"
      >
        <Modal.Header closeButton>
          <Modal.Title id="title-suggestions-heading">Title Suggestions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewUrl ? (
            <figure className="text-center mb-3">
              <img
                src={previewUrl}
                alt={`${previewTitle} preview`}
                style={{ maxWidth: "100%", maxHeight: "35vh", objectFit: "contain" }}
              />
            </figure>
          ) : (
            <p className="text-secondary">Artwork preview is unavailable.</p>
          )}
          {error || batch?.error ? <Alert variant="danger">{error || batch?.error}</Alert> : null}
          {busy ? (
            <p role="status">
              <Spinner as="span" size="sm" aria-hidden="true" /> Generating new suggestions… Your
              saved sets are kept.
            </p>
          ) : null}
          {batches.length > 1 ? (
            <Form.Group className="mb-3" controlId="title-suggestion-set">
              <Form.Label>Saved suggestion set</Form.Label>
              <Form.Select
                value={batchId}
                onChange={(event) => {
                  setBatchId(event.target.value);
                  setSelected("");
                }}
              >
                {batches.map((item, index) => (
                  <option key={item.id} value={item.id}>
                    {index === 0 ? "Latest" : "Earlier"} ·{" "}
                    {new Date(item.createdAt).toLocaleString()} · {item.state}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ) : null}
          {batch?.groups
            ? titleCategories.map((category) => (
                <fieldset className="mb-3" key={category}>
                  <legend className="h5 text-capitalize">{category}</legend>
                  {batch.groups![category].map((title, index) => (
                    <Form.Check
                      key={title}
                      type="radio"
                      id={`title-choice-${category}-${index}`}
                      name="title-suggestion"
                      label={title}
                      checked={selected === title}
                      onChange={() => setSelected(title)}
                    />
                  ))}
                  {!batch.groups![category].length ? (
                    <p className="text-secondary">No unused titles in this category.</p>
                  ) : null}
                </fieldset>
              ))
            : null}
          {batch?.groups && !hasTitles ? (
            <Alert variant="info">These titles are already in use. Generate a new set.</Alert>
          ) : null}
          <p className="small text-secondary">
            Titles already used by another artwork are hidden. Availability is checked again when
            you save.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-light" disabled={busy} onClick={() => void run(true)}>
            Generate New Suggestions
          </Button>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={!selected || disabled}
            onClick={() => {
              onUse(selected);
              setOpen(false);
            }}
          >
            Use This Title
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
